import * as React from 'react';
import type LocationCommonWellItemViewState from '../store/schema/LocationCommonWellItemViewState';
import ReadWriteCommonWellItem from 'owa-readwrite-common-well/lib/components/ReadWriteCommonWellItem';
import { classNamesFunction } from '@fluentui/utilities';
import { getStyles } from 'owa-readwrite-common-well/lib/components/ReadWriteCommonWellItem.styles';
import { LocationPersonaControl } from 'owa-location-persona';
import { observer } from 'mobx-react';
import { PersonaSize } from '@fluentui/react/lib/Persona';
import type {
    ReadWriteCommonWellItemStyles,
    ReadWriteCommonWellItemStyleProps,
    ReadWriteCommonWellItemProps,
} from 'owa-readwrite-common-well/lib/components/ReadWriteCommonWellItem.types';
import { RoomIcon } from 'owa-room-control';
import { LocationCard } from 'owa-location-card';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { getExtendedTheme } from 'owa-fabric-theme';
import { locationCommonWellItemRemoveButton } from './LocationCommonWellItem.locstring.json';
import loc from 'owa-localize';

let styles = require('./LocationCommonWellItem.scss');
let classNames = require('classnames');

const getClassNames = classNamesFunction<
    ReadWriteCommonWellItemStyleProps,
    ReadWriteCommonWellItemStyles
>();

export interface LocationCommonWellItemProps
    extends ReadWriteCommonWellItemProps<LocationCommonWellItemViewState> {
    colorScheme: ColorScheme;
    onRenderFBBadge: (emailAddress: string) => JSX.Element;
    itemClassName?: string;
}

@observer
export default class LocationCommonWellItem extends ReadWriteCommonWellItem<
    LocationCommonWellItemViewState,
    LocationCommonWellItemProps
> {
    protected getCustomizedClassName = (): {
        [key in keyof ReadWriteCommonWellItemStyles]: string;
    } => {
        let { theme, isSelected, isSmallSize, maxItemWidth, isItemAligned } = this.props;

        return getClassNames(getStyles, {
            isSmallSize: isSmallSize,
            isRegularSize: !isSmallSize,
            isSelected: isSelected,
            isValid: true,
            isExpanding: false,
            isExpandable: false,
            theme: theme ?? getExtendedTheme(),
            maxItemWidth: maxItemWidth,
            isItemAligned: isItemAligned,
        });
    };

    protected getPersonaControl = (): JSX.Element => {
        const { viewState, colorScheme } = this.props;
        if (!viewState || !viewState.persona) {
            return null;
        }
        const isRoom = viewState.persona.personaType == 'Room';
        let personaIcon = viewState.persona.postalAddress?.Latitude &&
            viewState.persona.postalAddress?.Longitude && (
                <LocationPersonaControl
                    className={styles.locationPersona}
                    locationPersona={viewState.persona}
                    size={PersonaSize.size32}
                />
            );

        if (isRoom) {
            personaIcon = (
                <div className={styles.locationPersona}>
                    <RoomIcon colorScheme={colorScheme} />
                    {this.props.onRenderFBBadge
                        ? this.props.onRenderFBBadge(viewState.persona.emailAddress?.EmailAddress)
                        : null}
                </div>
            );
        }

        const pillInfo = (
            <div
                className={classNames(
                    this.classNames.personaInfoWrapper,
                    viewState.blockWellItemRemoval ? styles.readOnlyPill : null
                )}>
                {personaIcon}
                <span className={this.classNames.wellItemText}>
                    {viewState.persona.displayName}
                </span>
            </div>
        );

        return (
            <LocationCard
                name={viewState.persona.displayName}
                postalAddress={viewState.persona.postalAddress}
                key={viewState.persona.personaId}
                locationId={viewState.persona.personaId}
                colorScheme={this.props.colorScheme}
                locationAriaLabel={viewState.persona.displayName}
                hoverTargetStyle={styles.hoverTargetStyle}>
                {pillInfo}
            </LocationCard>
        );
    };

    protected onFocusCapture = () => {
        this.wellItemBox.className = classNames(
            this.classNames.focusedWellItemBox,
            this.wellItemBox.className
        );
    };

    protected onFocusOut = () => {
        this.wellItemBox.className = this.classNames.wellItemBox;
    };

    protected getStringForRemoveButton = () => {
        return loc(locationCommonWellItemRemoveButton);
    };

    protected renderWellItem = () => {
        let pillContent = this.getRenderResult();
        let wellItem = (
            <div
                key={`locationwell_item_${this.props.index}`}
                ref={ref => (this.focusWrapper = ref)}
                className={this.props.itemClassName}
                onKeyDown={this.onKeyDown}
                aria-label={this.props.viewState?.displayText}
                role="listitem">
                {pillContent}
            </div>
        );

        return wellItem;
    };
}
